import { useModal } from '@context/ModalContext';
import IconMapM from '@firsttable/web-components/atoms/Icon/IconMapM';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import Button, { type ButtonProps } from '@discover-ui/button';
import { cn } from '@discover-lib/utils';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import useMapQueryState from './useMapQueryState';
import useSyncMapState from './syncMapState';
import MapModal from './modal';

export type MapMobileButtonProps = {
  defaultViewState: ViewState;
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
  showLabel?: boolean;
} & Omit<ButtonProps, 'onClick' | 'children'>;

export default function MapMobileButton({
  defaultViewState,
  tags,
  suburbs,
  peopleOptions,
  minDiners,
  maxDiners,
  showLabel = true,
  className,
  'aria-label': ariaLabel,
  ...props
}: MapMobileButtonProps) {
  const { showModal, hideModal, isModalOpen } = useModal();
  const [, setMapState] = useMapQueryState();
  const { track } = useAnalytics();
  useSyncMapState(defaultViewState, tags, suburbs, peopleOptions, minDiners, maxDiners);

  return (
    <Button
      onClick={() => {
        // showLabel is what separates the labelled chip from the icon-only button, so it also names the event — the two entry points are reported as separate metrics.
        track('button_clicked', {
          button_name: showLabel ? 'open map chip' : 'open map icon',
          button_type: 'navigation',
        });
        setMapState(true);
        if (!isModalOpen) {
          showModal(
            <MapModal
              defaultViewState={defaultViewState}
              tags={tags}
              suburbs={suburbs}
              peopleOptions={peopleOptions}
              minDiners={minDiners}
              maxDiners={maxDiners}
            />,
            {
              showCloseIcon: false,
              disableEscExit: true,
              onRequestClose: hideModal,
              hideOnRouteChange: false,
            }
          );
        }
      }}
      className={cn('gap-1', className)}
      aria-label={ariaLabel ?? (showLabel ? undefined : 'View map')}
      {...props}>
      {showLabel && 'View map'}
      <IconMapM className="size-5" color="inherit" />
    </Button>
  );
}
